import { useState } from 'react';
import { X, TableIcon, Loader2 } from 'lucide-react';
import ManagerApprovalModal from './ManagerApprovalModal';
import { posOrders } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';

export default function MoveTableModal({ order, tables, onClose, onMoved }) {
  const { posToken } = usePosStore();
  const [selected, setSelected] = useState(null);
  const [showApproval, setShowApproval] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const available = tables.filter(t => t.status === 'available');

  const handleConfirm = () => {
    if (!selected) {
      setError('Select a destination table');
      return;
    }
    setShowApproval(true);
  };

  const handleApproved = async (approvedBy) => {
    setShowApproval(false);
    setLoading(true);
    setError(null);
    try {
      const res = await posOrders.transferTable(posToken, order._id, {
        toTableId: selected._id,
        approvedBy,
      });
      onMoved(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Move failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="font-bold text-gray-900">Move Order — Select New Table</h2>
              <p className="text-xs text-gray-500 mt-0.5">From: {order.tableName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {error && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            {available.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-400 gap-2">
                <TableIcon className="h-8 w-8" />
                <p className="text-sm">No available tables</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {available.map((t) => {
                  const isSelected = selected?._id === t._id;
                  return (
                    <button
                      key={t._id}
                      onClick={() => setSelected(t)}
                      className={`h-20 rounded-xl border-2 flex flex-col items-center justify-center gap-1 text-sm font-semibold transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-800 ring-2 ring-blue-300'
                          : 'border-green-400 bg-green-50 text-green-800 hover:bg-green-100'
                      }`}
                    >
                      <TableIcon className="h-5 w-5" />
                      <span>{t.name}</span>
                      {t.capacity && (
                        <span className="text-[10px] font-normal text-gray-400">{t.capacity}p</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 px-5 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selected || loading}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? 'Moving…' : `Move to ${selected?.name || '…'}`}
            </button>
          </div>
        </div>
      </div>

      <ManagerApprovalModal
        isOpen={showApproval}
        action="move_table"
        onApprove={handleApproved}
        onClose={() => setShowApproval(false)}
      />
    </>
  );
}
